import R from 'ramda'
import { createKey, createIV, createSalt } from '../core/random'
import { createSymmetricEncryptionKeyFromPublicKey } from '../core/ecc'
import { encryptKey } from './header'
import { encrypt as encryptData } from './data'
import { createBlock, createEncryptionParams, createDerivationParams } from './format'

/**
 * Create Object containing encryptionParams
 *
 * @return {{encryptionParams: SymmetricEncryptionParams}}
 * @private
 */
export function _createEncryptionParamsObj () {
  return R.compose(R.objOf('encryptionParams'), createEncryptionParams, createIV)()
}

/**
 * Prepare params for symmetric encryption
 *
 * @param {{password: String, hint: String, iterations: Number}[]} passwords passwords for symmetric encryption
 * @return {{derivationParams: DerivationParams, encryptionParams: SymmetricEncryptionParams, password: String, hint: String}[]}
 * @private
 */
export function _createSymmetricEncryptionParams (passwords) {
  return R.map(
    R.converge(
      R.unapply(R.mergeAll),
      [
        R.compose(R.objOf('derivationParams'),
          R.converge(
            createDerivationParams,
            [
              R.prop('iterations'),
              createSalt
            ]
          )
        ),
        _createEncryptionParamsObj,
        R.identity
      ]
    )
  )(passwords)
}

/**
 * Prepare params for symmetric encryption
 *
 * @param {{id: String, version: String, key: ElGamalPublicKey}[]} publicKeys public keys for asymmetric encryption
 * @return {{tag: Base64UrlString, key: Base64UrlString, encryptionParams: SymmetricEncryptionParams, id: String, version: String}[]}
 * @private
 */
export function _createAsymmetricEncryptionParams (publicKeys) {
  return R.map(
    R.converge(
      R.unapply(R.mergeAll),
      [
        R.compose(createSymmetricEncryptionKeyFromPublicKey, R.prop('key')),
        _createEncryptionParamsObj,
        R.pick([ 'id', 'version' ])
      ]
    )
  )(publicKeys)
}

/**
 * Prepare params for files
 *
 * @param {{name: String, size: Number, type: String, id: String, path: String, service: String, data: ArrayBuffer}[]} files
 * @return {{name: String, size: Number, type: String, id: String, data: ArrayBuffer, encryptionParams: SymmetricEncryptionParams}[]}
 * @private
 */
export function _createFileParams (files) {
  return R.map(
    R.converge(
      R.unapply(R.mergeAll),
      [
        R.identity,
        _createEncryptionParamsObj
      ]
    )
  )(files)
}

/**
 * Encrypt block
 *
 * @param {{password: String, hint: String}[]} passwords passwords for symmetric encryption
 * @param {{id: String, version: String, key: ElGamalPublicKey}[]} publicKeys public keys for asymmetric encryption
 * @param {String} text
 * @param {{name: String, size: Number, type: String, id: String, path: String, service: String, data: ArrayBuffer}[]} files
 * @return {Block} Block object consists of encrypted parts
 * @throws {Error} if a parameter is invalid
 * @private
 */
export function encrypt (passwords, publicKeys, text, files) {
  return R.converge(
    R.compose(R.apply(createBlock), R.useWith(R.concat, [R.of, R.props(['data', 'files'])])),
    [
      R.curry(encryptKey)(R.__, R.__, R.__, _createSymmetricEncryptionParams(passwords), _createAsymmetricEncryptionParams(R.dropRepeatsWith(R.eqProps('key'), publicKeys))),
      R.curry(encryptData)(R.__, R.__, text, R.__)
    ]
  )(createKey(), createEncryptionParams(createIV()), _createFileParams(files))
}
